import { BehaviorSubject, distinctUntilChanged, Subject } from 'rxjs';
import {
  BlackjackAssetType,
  BlackjackStylesType,
  BlackjackTranslationsType,
} from '../../../model/blackjack-types';
import { GameServices } from '../../../services/game-services';
import { Blackjack } from 'game-logic';
import { ChipButtonControlViewModel, ChipData } from './chip-button-control-view-model';
import { RoundedNumberViewModel } from '../../components/rounded-number-view-model';

export class ChipPickerViewModel {
  public get placeYourBetText() {
    return this.gameServices.gameTranslationService.getTranslation(
      BlackjackTranslationsType.PlaceYourBet,
    );
  }
  public get defaultFontFamily() {
    return this.gameServices.gameThemeService.defaultFontFamily;
  }
  public get labelFill() {
    return this.gameServices.gameThemeService.getHexColor(BlackjackStylesType.LabelFill);
  }
  public get onGameSetupStart$() {
    return this.gameServices.blackjackGameRunner.onGameSetupStart$;
  }
  public get logicEventQueue$() {
    return this.gameServices.blackjackGameRunner.logicEventQueue$;
  }
  public get interfaceEventQueue() {
    return this.gameServices.interfaceEventQueue;
  }
  public get onResetGameScene$() {
    return this.onResetGameScene;
  }
  public get showControl$() {
    return this.showControl.asObservable().pipe(distinctUntilChanged());
  }

  public readonly chipAmountViewModels: ChipButtonControlViewModel[];
  public readonly balanceViewModel: RoundedNumberViewModel;

  private readonly onResetGameScene = new Subject<void>();
  private readonly showControl: BehaviorSubject<boolean>;
  private canAnimateChips = true;

  constructor(private gameServices: GameServices) {
    const chipAmounts: ChipData[] = [
      { chip: Blackjack.ChipDenomination.Chip10, asset: BlackjackAssetType.Chip10 },
      { chip: Blackjack.ChipDenomination.Chip50, asset: BlackjackAssetType.Chip50 },
      { chip: Blackjack.ChipDenomination.Chip100, asset: BlackjackAssetType.Chip100 },
      { chip: Blackjack.ChipDenomination.Chip500, asset: BlackjackAssetType.Chip500 },
      { chip: Blackjack.ChipDenomination.Chip1k, asset: BlackjackAssetType.Chip1k },
      { chip: Blackjack.ChipDenomination.Chip5k, asset: BlackjackAssetType.Chip5k },
      { chip: Blackjack.ChipDenomination.Chip10k, asset: BlackjackAssetType.Chip10k },
      { chip: Blackjack.ChipDenomination.Chip50k, asset: BlackjackAssetType.Chip50k },
      { chip: Blackjack.ChipDenomination.Chip100k, asset: BlackjackAssetType.Chip100k },
    ];
    this.chipAmountViewModels = chipAmounts.map(
      (data) =>
        new ChipButtonControlViewModel(gameServices, data, (amount) =>
          this.chipAmountButtonClicked(amount),
        ),
    );
    this.balanceViewModel = new RoundedNumberViewModel(
      gameServices,
      BlackjackTranslationsType.Balance,
      0,
      gameServices.gameThemeService.getNumericalRGB(BlackjackStylesType.CardBackground),
      gameServices.gameThemeService.getNumericalAlpha(BlackjackStylesType.CardBackground),
    );
    this.showControl = new BehaviorSubject<boolean>(false);
  }

  public resetGameScene() {
    this.onResetGameScene.next();

    this.canAnimateChips = true;
    this.balanceViewModel.setNumber(0);
    this.showControl.next(false);
  }

  public roundStateChanged(state: Blackjack.RoundState) {
    this.showControl.next(state === Blackjack.RoundState.Deal);
  }

  public setBalanceAmount(balance: number): void {
    this.balanceViewModel.setNumber(balance);
  }

  public resetChipAnimation(): void {
    this.canAnimateChips = true;
  }

  public animateChipButtons(enable: boolean): void {
    if (enable && !this.canAnimateChips) {
      return;
    }

    this.chipAmountViewModels.forEach((view) => view.enableButtonAnimation(enable));

    if (enable) {
      this.canAnimateChips = false;
    }
  }

  private chipAmountButtonClicked(amount: Blackjack.ChipDenomination) {
    this.gameServices.interfaceController.increaseCurrentBet(amount);
  }
}
